import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { DATABASE } from '../Config/firebase';

/**
 * Hook to track pending deposits and withdrawals in real-time
 * Returns counts of pending transactions
 * 
 * Note: Uses transection_requests collection (not separate deposit/withdraw collections)
 * - Deposits: request_type == 0
 * - Withdrawals: request_type == 1, otp_verify == true
 */
export const usePendingTransactions = () => {
  const [pendingDeposits, setPendingDeposits] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Query for all deposits (request_type == 0)
    // We'll filter by status in memory to avoid complex index requirements
    const allDepositsQuery = query(
      collection(DATABASE, 'transection_requests'),
      where('request_type', '==', 0)
    );

    // Query for all withdrawals (request_type == 1, otp_verify == true)
    // We'll filter by status in memory to avoid complex index requirements
    const allWithdrawalsQuery = query(
      collection(DATABASE, 'transection_requests'),
      where('request_type', '==', 1),
      where('otp_verify', '==', true)
    );

    // Subscribe to all deposits and filter by status
    const unsubscribeDeposits = onSnapshot(
      allDepositsQuery,
      (snapshot) => {
        const pendingDocs = snapshot.docs.filter(
          doc => {
            const status = doc.data().request_status;
            // Status 0: Pending, Status 1: Waiting - both need admin action
            return status === 0 || status === 1;
          }
        );
        const count = pendingDocs.length;
        
        // Only log if there are pending deposits (keep it minimal)
        if (count > 0) {
          console.log(`[Pending] ${count} deposit(s) awaiting approval`);
        }
        
        setPendingDeposits(count);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching pending deposits:', error);
        setLoading(false);
      }
    );

    // Subscribe to all withdrawals and filter by status
    const unsubscribeWithdrawals = onSnapshot(
      allWithdrawalsQuery,
      (snapshot) => {
        const pendingDocs = snapshot.docs.filter(
          doc => {
            const status = doc.data().request_status;
            
            // Status 0: Pending (OTP not confirmed) - does NOT need admin action yet
            // Status 1: OTP confirmed, awaiting approval - NEEDS admin action
            // Status 2: Approved, awaiting payment - NEEDS admin action (mark as paid)
            // Status 3: Cancelled by user - NO action needed
            // Status 4: Rejected - NO action needed
            // Status 5: Paid - NO action needed
            
            return status === 1 || status === 2;
          }
        );
        const count = pendingDocs.length;
        
        // Only log if there are pending withdrawals (keep it minimal)
        if (count > 0) {
          console.log(`[Pending] ${count} withdrawal(s) awaiting action`);
        }
        
        setPendingWithdrawals(count);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching pending withdrawals:', error);
        setLoading(false);
      }
    );

    // Cleanup subscriptions
    return () => {
      unsubscribeDeposits();
      unsubscribeWithdrawals();
    };
  }, []);

  return {
    pendingDeposits,
    pendingWithdrawals,
    loading,
    hasPending: pendingDeposits > 0 || pendingWithdrawals > 0
  };
};
